"use client";

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useRouter } from "next/navigation";
import Header from "../../components/contentparts/Header";
import ChaletSearchResults from "../../components/DynamicPages/Search/ChaletSearch/ChaletSearchResults";
import DynamicSearchQueryBuilder from "../../components/DynamicSearchQueryBuilder";
import type { SearchQuery } from "@/types/searchquery";

const apiUrl = process.env.NEXT_PUBLIC_REACT_APP_API_URL;

interface ResortData {
  id: number;
  name: string;
  description: string;
  location: string;
  imageUrl: string;
}

const ResortClient: React.FC = () => {
  const params = useParams();
  const resortParam = Array.isArray(params.resort)
    ? params.resort[0]
    : params.resort;
  const [resortData, setResortData] = useState<ResortData | null>(null);
  const [searchQuery, setSearchQuery] = useState<SearchQuery | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  // Fetch the resort data on mount
  useEffect(() => {
    if (!resortParam) return;

    const fetchResortData = async () => {
      try {
        const response = await fetch(
          `${apiUrl}/GetResortByName/${encodeURIComponent(resortParam)}`
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch resort data: ${response.statusText}`
          );
        }

        const data = await response.json();

        if (data && data.id) {
          setResortData(data);
          document.title = `Chalets in ${data.name}`;
        } else {
          setError("Resort data not found.");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load resort data. Please try again later.");
        router.push("/"); // ⛷️ send user back to homepage
      } finally {
        setLoading(false);
      }
    };

    fetchResortData();
  }, [resortParam, router]);

  // Handle the initial search query from the query builder
  const handleChildData = useCallback((queryStringParams: SearchQuery) => {
    setSearchQuery(queryStringParams);
  }, []);

  return (
    <div>
      {/* Only render the query builder once the resort data is loaded */}
      {resortData && (
        <DynamicSearchQueryBuilder
          sendDataToParent={handleChildData}
          queryResortId={resortData.id}
          queryCountryName={resortData.location} // Optional, in case you want to support countries
        />
      )}

      <Header
        PageName={`Chalets in ${
          resortParam
            ? resortParam.charAt(0).toUpperCase() + resortParam.slice(1)
            : "Austria"
        }`}
        PageBreadCrumbName={`${
          resortParam
            ? resortParam.charAt(0).toUpperCase() + resortParam.slice(1)
            : "Austria"
        } Chalet Search`}
      />

      {/* Only render ChaletSearchResults if the searchQuery is ready */}
      {searchQuery && (
        <ChaletSearchResults
          SearchQuery={searchQuery}
          pageHeadertext={`Please browse Chalets in ${
            resortParam
              ? resortParam.charAt(0).toUpperCase() + resortParam.slice(1)
              : "Austria"
          }`}
        />
      )}
      {/* Basic error handling */}
      {loading && <p>Loading resort information...</p>}
      {error && <p>{error}</p>}
    </div>
  );
};

export default ResortClient;
